/**
 * quantTools.js — два количественных инструмента: CAD-RADS и карты T1/T2.
 *
 * Здесь ТОЛЬКО интерфейс: весь счёт живёт в чистых модулях, которые проверяются
 * без браузера — `core/cadRads.js` и `core/relaxometry.js`. Разделение не ради
 * красоты: границы CAD-RADS и восстановление T1/T2 из кривой обязаны проверяться
 * числом на известном ответе, а разглядывание окна этого не даёт.
 *
 * ЧТО ЭТИ КНОПКИ НЕ ДЕЛАЮТ, и это написано в самих окнах:
 *   · CAD-RADS — калькулятор по процентам стеноза, которые ВВЁЛ ВРАЧ.
 *     Автоматического измерения стеноза в программе нет.
 *   · Карты T1/T2 — моноэкспоненциальная модель без коррекции B1; MOLLI
 *     считается другой моделью, и об этом окно предупреждает отдельно.
 */

import { рассчитать as рассчитатьCadRads, ПРИЗНАКИ_HRP } from '../core/cadRads.js';
import { картаT2, картаT1, предупрежденияПоСерии, среднееПоМаске } from '../core/relaxometry.js';
import { картаT1MOLLI, похожеНаMOLLI, оговоркиMOLLI } from '../core/molliT1.js';
import { getSeriesImageIds, getAllSeries, getDICOMTags } from '../loaders/loaderManager.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

const cs = () => window.cornerstone || null;
const $ = (id) => document.getElementById(id);

// ══════════════════════════════════════════════════════════════
//  Общая рамка окна
// ══════════════════════════════════════════════════════════════

export function окно(заголовок, { ширина = 560 } = {}) {   // [12.0.2] рамка общая: ПЭТ/перфузия берут её отсюда, второй не заводят
  const фон = document.createElement('div');
  фон.className = 'quant-modal';
  фон.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;'
    + 'justify-content:center;background:rgba(4,8,14,.72);backdrop-filter:blur(2px);';
  const карта = document.createElement('div');
  карта.style.cssText = `background:#111926;border:1px solid #2a3546;border-radius:14px;padding:16px;`
    + `max-width:${ширина}px;width:calc(100vw - 32px);max-height:calc(100vh - 40px);overflow:auto;`
    + 'box-shadow:0 20px 60px rgba(0,0,0,.6);color:#e7eefc;font:13px/1.5 system-ui,Segoe UI,sans-serif;';
  const шапка = document.createElement('div');
  шапка.style.cssText = 'font-size:16px;font-weight:700;margin-bottom:10px;';
  шапка.textContent = заголовок;
  const тело = document.createElement('div');
  const низ = document.createElement('div');
  низ.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;margin-top:14px;';
  const закрыть = document.createElement('button');
  закрыть.textContent = 'Закрыть';
  закрыть.style.cssText = 'background:#1d2939;border:1px solid #33415a;color:#e7eefc;border-radius:8px;padding:7px 14px;cursor:pointer;';
  закрыть.addEventListener('click', () => фон.remove());
  низ.appendChild(закрыть);
  карта.append(шапка, тело, низ);
  фон.appendChild(карта);
  фон.addEventListener('click', (e) => { if (e.target === фон) фон.remove(); });
  document.body.appendChild(фон);
  return { фон, карта, тело, низ, закрыть };
}

export const строкаВвода = (подпись, id, значение, доп = '') => `
  <label style="display:flex;align-items:center;gap:8px;margin:5px 0;">
    <span style="min-width:210px;color:#9fb3cf;">${подпись}</span>
    <input id="${id}" value="${значение}" ${доп}
      style="flex:1;background:#0c1420;border:1px solid #2a3546;color:#e7eefc;border-radius:7px;padding:5px 8px;">
  </label>`;

// ══════════════════════════════════════════════════════════════
//  CAD-RADS 2.0
// ══════════════════════════════════════════════════════════════

export function открытьCadRads() {
  const w = окно('CAD-RADS 2.0 — калькулятор', { ширина: 620 });
  w.тело.innerHTML = `
    <div style="background:#1a2433;border-left:3px solid #f0b429;padding:8px 10px;border-radius:6px;margin-bottom:12px;color:#ffd98a;">
      Расчёт по процентам стеноза, которые вводите вы. Автоматического измерения
      стеноза в программе нет. CAD-RADS не заменяет заключение врача.
    </div>
    <div style="font-weight:600;margin:6px 0;">Стеноз по сосудам, %</div>
    ${строкаВвода('ПМЖА (LAD)', 'cr-lad', '0', 'type="number" min="0" max="100"')}
    ${строкаВвода('Огибающая (LCx)', 'cr-lcx', '0', 'type="number" min="0" max="100"')}
    ${строкаВвода('ПКА (RCA)', 'cr-rca', '0', 'type="number" min="0" max="100"')}
    ${строкаВвода('Ствол ЛКА', 'cr-lm', '0', 'type="number" min="0" max="100"')}
    ${строкаВвода('Невыводимых сегментов', 'cr-nd', '0', 'type="number" min="0" max="18"')}
    <div style="font-weight:600;margin:10px 0 6px;">Нагрузка бляшками (одно из двух)</div>
    ${строкаВвода('Кальциевый счёт (Агатстон)', 'cr-cac', '', 'type="number" min="0"')}
    ${строкаВвода('SIS — сегментов с бляшкой', 'cr-sis', '', 'type="number" min="0" max="17"')}
    <div style="font-weight:600;margin:10px 0 6px;">Признаки уязвимой бляшки (нужно два и более)</div>
    <div id="cr-hrp" style="display:flex;flex-direction:column;gap:4px;color:#c8d6ea;">
      ${Object.entries(ПРИЗНАКИ_HRP).map(([ид, текст]) => `
        <label style="display:flex;gap:8px;align-items:center;">
          <input type="checkbox" data-hrp="${ид}"><span>${текст}</span>
        </label>`).join('')}
    </div>
    <div style="font-weight:600;margin:10px 0 6px;">Прочее</div>
    ${строкаВвода('CT-FFR (0…1), если считался', 'cr-ffr', '', 'type="number" min="0" max="1" step="0.01"')}
    <label style="display:flex;gap:8px;align-items:center;margin:5px 0;"><input type="checkbox" id="cr-stent"><span>есть стент (S)</span></label>
    <label style="display:flex;gap:8px;align-items:center;margin:5px 0;"><input type="checkbox" id="cr-graft"><span>есть шунт (G)</span></label>
    ${строкаВвода('Неатеросклеротическая причина (E)', 'cr-exc', '')}
    <div id="cr-out" style="margin-top:14px;padding:12px;background:#0c1420;border:1px solid #2a3546;border-radius:10px;"></div>
  `;

  const копировать = document.createElement('button');
  копировать.textContent = 'Скопировать запись';
  копировать.style.cssText = 'background:#1e40af;border:1px solid #2b56d4;color:#fff;border-radius:8px;padding:7px 14px;cursor:pointer;';
  w.низ.insertBefore(копировать, w.закрыть);

  let последняя = null;

  const посчитать = () => {
    const ч = (id) => { const v = $(id)?.value?.trim(); return v === '' || v == null ? null : Number(v); };
    const сосуды = [
      { имя: 'ПМЖА', стеноз: ч('cr-lad') ?? 0 },
      { имя: 'ОА', стеноз: ч('cr-lcx') ?? 0 },
      { имя: 'ПКА', стеноз: ч('cr-rca') ?? 0 },
    ];
    const признаки = [...w.тело.querySelectorAll('#cr-hrp input[data-hrp]')]
      .filter((э) => э.checked).map((э) => э.dataset.hrp);
    const р = рассчитатьCadRads({
      сосуды,
      стволЛКА: ч('cr-lm') ?? 0,
      недиагностическиеСегменты: ч('cr-nd') ?? 0,
      cac: ч('cr-cac'), sis: ч('cr-sis'),
      признакиHRP: признаки,
      ctFFR: ч('cr-ffr'),
      стент: !!$('cr-stent')?.checked,
      шунт: !!$('cr-graft')?.checked,
      исключение: $('cr-exc')?.value?.trim() || null,
    });
    последняя = р;
    const вывод = $('cr-out');
    if (!вывод) return;
    вывод.innerHTML = `
      <div style="font-size:20px;font-weight:700;color:#7ee787;">${р.запись || '—'}</div>
      <div style="margin-top:6px;color:#c8d6ea;">Максимальный стеноз: ${р.максСтеноз ?? '—'}%
        · главных сосудов ≥70%: ${р.обструктивныхГлавных}${р.стволОбструктивный ? ' · ствол ЛКА ≥50%' : ''}</div>
      <div style="margin-top:8px;"><b>Тактика:</b> ${р.ведение || '—'}</div>
      ${р.ошибки.length ? `<div style="margin-top:8px;color:#ff9c9c;"><b>Ошибки ввода:</b><br>${р.ошибки.join('<br>')}</div>` : ''}
      <details style="margin-top:8px;"><summary style="cursor:pointer;color:#9fb3cf;">Оговорки (${р.оговорки.length})</summary>
        <div style="color:#9fb3cf;margin-top:6px;">${р.оговорки.join('<br>')}</div></details>`;
  };

  w.тело.addEventListener('input', посчитать);
  w.тело.addEventListener('change', посчитать);
  посчитать();

  копировать.addEventListener('click', async () => {
    if (!последняя?.запись) { showToast('Нечего копировать', 'warning', 1500); return; }
    const текст = `${последняя.запись}\nТактика: ${последняя.ведение}\n`
      + `Максимальный стеноз ${последняя.максСтеноз}%. Расчёт по введённым значениям; CAD-RADS не заменяет заключение врача.`;
    try {
      await navigator.clipboard.writeText(текст);
      showToast('Запись CAD-RADS скопирована', 'success', 1800);
    } catch (e) {
      showToast('Копировать не удалось: ' + e.message, 'error', 3000);
    }
  });

  return w;
}

// ══════════════════════════════════════════════════════════════
//  Карты T1/T2
// ══════════════════════════════════════════════════════════════

/** Прочитать TE/TI и позицию срезов серии. */
async function кадрыСерии(seriesUID, какой) {
  const imageIds = getSeriesImageIds?.(seriesUID) || [];
  if (!imageIds.length) return { кадры: [], причина: 'в серии нет кадров' };

  const адрес = какой === 't1' ? 'x00180082' : 'x00180081';
  const собрано = [];
  for (const id of imageIds.slice(0, 64)) {           // 64 кадра хватает на любую многоэховую серию
    let теги = {};
    try { теги = await getDICOMTags(id); } catch (e) { log.debug('[Карта] теги', e.message); }
    const t = Number(теги?.[адрес]?.value);
    const позиция = String(теги?.['x00200032']?.value || '');
    if (!Number.isFinite(t) || t <= 0) continue;
    собрано.push({ id, t, позиция });
  }
  if (!собрано.length) return { кадры: [], причина: какой === 't1' ? 'в серии нет времени инверсии (TI)' : 'в серии нет времени эха (TE)' };

  // Группируем по положению срезa: карту строим там, где на одном месте
  // несколько разных TE/TI. Иначе «многоэховость» окажется просто разными
  // срезами, и карта получится из разных анатомий.
  const группы = new Map();
  for (const к of собрано) {
    const ключ = к.позиция || 'без-позиции';
    if (!группы.has(ключ)) группы.set(ключ, []);
    группы.get(ключ).push(к);
  }
  let лучшая = [];
  for (const г of группы.values()) {
    const разных = new Set(г.map((к) => к.t)).size;
    if (разных > new Set(лучшая.map((к) => к.t)).size) лучшая = г;
  }
  const разныхВремён = new Set(лучшая.map((к) => к.t)).size;
  if (разныхВремён < 3) {
    return {
      кадры: [],
      причина: `на одном срезе найдено ${разныхВремён} значени(я) ${какой === 't1' ? 'TI' : 'TE'} — для карты нужно минимум три`,
    };
  }

  // Пиксели.
  const кадры = [];
  for (const к of лучшая.sort((a, b) => a.t - b.t)) {
    try {
      const image = await cs().imageLoader.loadAndCacheImage(к.id);
      const пиксели = image.getPixelData();
      const наклон = Number.isFinite(image.slope) ? image.slope : 1;
      const сдвиг = Number.isFinite(image.intercept) ? image.intercept : 0;
      const данные = new Float32Array(пиксели.length);
      for (let i = 0; i < пиксели.length; i++) данные[i] = пиксели[i] * наклон + сдвиг;
      кадры.push(какой === 't1' ? { ti: к.t, данные, ширина: image.columns, высота: image.rows }
        : { te: к.t, данные, ширина: image.columns, высота: image.rows });
    } catch (e) {
      log.warn('[Карта] кадр не загрузился:', e.message);
    }
  }
  if (кадры.length < 3) return { кадры: [], причина: 'пиксели кадров не загрузились' };
  return { кадры, причина: null };
}

/** Раскрасить карту: синий → зелёный → жёлтый → красный. */
export function нарисовать(карта, ширина, высота, низ, верх) {
  const c = document.createElement('canvas');
  c.width = ширина; c.height = высота;
  c.style.cssText = 'width:100%;image-rendering:pixelated;border-radius:8px;border:1px solid #2a3546;';
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(ширина, высота);
  const диапазон = верх - низ || 1;
  for (let i = 0; i < ширина * высота; i++) {
    const v = карта[i];
    const o = i * 4;
    if (!Number.isFinite(v)) { img.data[o] = 12; img.data[o + 1] = 18; img.data[o + 2] = 28; img.data[o + 3] = 255; continue; }
    const t = Math.max(0, Math.min(1, (v - низ) / диапазон));
    const r = t < 0.5 ? 0 : Math.round((t - 0.5) * 2 * 255);
    const g = t < 0.5 ? Math.round(t * 2 * 255) : Math.round(255 - (t - 0.5) * 2 * 90);
    const b = t < 0.5 ? Math.round(255 - t * 2 * 255) : 0;
    img.data[o] = r; img.data[o + 1] = g; img.data[o + 2] = b; img.data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

/**
 * Медиана по конечным значениям карты — для подписи. [10.19.22]
 * Медиана, а не среднее: у T1* и B/A хвосты от вокселей на границе стенки,
 * и среднее по ним уезжает, хотя середина среза при этом в порядке.
 */
function сводкаКонечных(массив, знаков = 0) {
  if (!массив?.length) return '—';
  const з = [...массив].filter(Number.isFinite).sort((a, b) => a - b);
  if (!з.length) return '—';
  const м = з[Math.floor(з.length / 2)];
  return знаков ? м.toFixed(знаков) : Math.round(м) + ' мс';
}

export async function построитьКарту(какой) {
  const серии = (() => { try { const s = getAllSeries?.() || []; return Array.isArray(s) ? s : Object.values(s); } catch { return []; } })();
  if (!серии.length) { showToast('Сначала откройте исследование', 'warning', 3000); return null; }

  // Берём серию МРТ: у КТ ни TE, ни TI не бывает.
  const мр = серии.filter((с) => String(с.modality || '').toUpperCase() === 'MR');
  const цель = (мр.length ? мр : серии)[мр.length ? мр.length - 1 : серии.length - 1];
  const uid = цель.seriesUID || цель.seriesInstanceUID;
  if (!uid) { showToast('У серии нет идентификатора', 'warning', 3000); return null; }
  if (!мр.length) {
    showToast('Карты T1/T2 считаются по МР-серии: в исследовании МР-серий не нашлось', 'warning', 5000);
    return null;
  }

  showToast(`Собираю кадры серии для карты ${какой.toUpperCase()}…`, 'info', 2500);
  const { кадры, причина } = await кадрыСерии(uid, какой);
  if (!кадры.length) {
    showToast(`Карта ${какой.toUpperCase()} не построена: ${причина}`, 'warning', 7000);
    return null;
  }

  // [10.19.22] Модель выбирается ПО СЕРИИ, а не одна на всё.
  //
  // Раньше любая T1-серия считалась двухпараметрической инверсией-восстановлением,
  // и MOLLI — то, чем T1 миокарда мерят на практике, — уходил в неподходящую
  // модель. Она закрепляет B/A = 2 и не отдаёт ни T1*, ни отношение, то есть по
  // её ответу нельзя понять, насколько он верен (замеры — в tests/molli_test.mjs).
  const описание = цель.seriesDescription || '';
  const этоMOLLI = какой === 't1' && похожеНаMOLLI(описание).molli;
  const результат = какой !== 't1' ? картаT2(кадры)
    : (этоMOLLI ? картаT1MOLLI(кадры) : картаT1(кадры));
  if (!результат.надёжно) {
    showToast(`Карта ${какой.toUpperCase()} не построена: ${результат.причина}`, 'warning', 7000);
    return null;
  }

  const значения = какой === 't1' ? результат.t1 : результат.t2;
  const ср = среднееПоМаске(значения, null, результат.r2, 0.9);
  const конечные = [...значения].filter(Number.isFinite).sort((a, b) => a - b);
  const низ = конечные[Math.floor(конечные.length * 0.02)] ?? 0;
  const верх = конечные[Math.floor(конечные.length * 0.98)] ?? 1;

  const предупреждения = этоMOLLI ? оговоркиMOLLI(описание) : предупрежденияПоСерии(описание);
  const w = окно(`Карта ${какой.toUpperCase()} — ${описание || 'МР-серия'}`, { ширина: 640 });
  const холст = нарисовать(значения, кадры[0].ширина, кадры[0].высота, низ, верх);

  const подпись = document.createElement('div');
  подпись.style.cssText = 'margin-top:10px;color:#c8d6ea;';
  подпись.innerHTML = `
    <div><b>${какой === 't1' ? 'T1' : 'T2'} по срезу:</b> среднее ${Number.isFinite(ср.среднее) ? ср.среднее.toFixed(1) : '—'} мс
      ± ${Number.isFinite(ср.разброс) ? ср.разброс.toFixed(1) : '—'} мс (вокселей с R²≥0,9: ${ср.вокселей})</div>
    <div>Кадров в подгонке: ${кадры.length} · ${какой === 't1' ? 'TI' : 'TE'}:
      ${(какой === 't1' ? результат.ti : результат.эхо).map((т) => т + ' мс').join(', ')}</div>
    <div>Посчитано вокселей: ${результат.посчитано} · отказов: ${результат.отказов} (у них NaN, а не ноль)</div>
    ${этоMOLLI ? `<div style="margin-top:4px;color:#a8e6b8;">Модель: ${результат.модель}.
      Кажущееся T1* по срезу: ${сводкаКонечных(результат.t1з)} · B/A: ${сводкаКонечных(результат.отношение, 2)}.
      Поправка Лука–Локера применена: без неё ответ был бы равен T1*.
      ${Object.keys(результат.отказыПоПричинам || {}).length
        ? 'Отказы по причинам: ' + Object.entries(результат.отказыПоПричинам).map(([п, н]) => `${п} — ${н}`).join(', ') + '.'
        : ''}</div>` : ''}
    <div style="margin-top:6px;color:#9fb3cf;">Шкала цвета: синий ${Math.round(низ)} мс → красный ${Math.round(верх)} мс.
      Тёмные точки — воксели, где модель не подошла.</div>`;

  const оговорка = document.createElement('div');
  оговорка.style.cssText = 'margin-top:10px;background:#1a2433;border-left:3px solid #f0b429;padding:8px 10px;border-radius:6px;color:#ffd98a;';
  // [10.19.22] Оговорка обязана называть ТУ модель, которой считали. Общая
  // строка «моноэкспоненциальная без коррекции B1» на карте MOLLI была бы
  // неправдой: там три параметра и поправка Лука–Локера.
  оговорка.innerHTML = `${этоMOLLI
    ? 'Трёхпараметрическая модель Look-Locker с поправкой T1 = T1*·(B/A − 1). Коррекции движения и B1 нет.'
    : 'Моноэкспоненциальная модель без коррекции B1.'} Не для диагностики.
    ${предупреждения.length ? '<br><b>Особенности этой серии:</b><br>' + предупреждения.join('<br>') : ''}`;

  w.тело.append(холст, подпись, оговорка);
  showToast(`Карта ${какой.toUpperCase()} построена: ${результат.посчитано} вокселей`, 'success', 3000);
  return { результат, ср };
}

// ══════════════════════════════════════════════════════════════
//  Привязка кнопок
// ══════════════════════════════════════════════════════════════

let _ac = null;

export function initQuantTools() {
  if (_ac) _ac.abort();
  _ac = new AbortController();
  const s = _ac.signal;

  $('btn-cadrads')?.addEventListener('click', () => { открытьCadRads(); }, { signal: s });
  $('btn-map-t2')?.addEventListener('click', () => { построитьКарту('t2'); }, { signal: s });
  $('btn-map-t1')?.addEventListener('click', () => { построитьКарту('t1'); }, { signal: s });

  log.info('[Количественные] CAD-RADS и карты T1/T2 привязаны');
}

export function destroyQuantTools() { if (_ac) { _ac.abort(); _ac = null; } }

export default { initQuantTools, destroyQuantTools, открытьCadRads, построитьКарту };
